"use client";

import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { selectOrganization } from "@/store/modules/workspace/workspace.selectors";
import { setOrganization } from "@/store/modules/workspace/workspace.actions";
import { isOrganizationDefaultLedgersComplete } from "@sdk/default-ledger-accounts/default-ledger-accounts-model";
import { WORKSPACE_DEFAULT_LEDGER_DEFINITIONS } from "@sdk/default-ledger-accounts/default-ledger-accounts-model";
import {
  WorkspaceBasicsForm,
  type WorkspaceBasicsFormValues,
} from "../workspace-basics-form";

type WorkspaceBasicsStepProps = {
  onSaved: () => void;
};

export function WorkspaceBasicsStep({ onSaved }: WorkspaceBasicsStepProps) {
  const dispatch = useDispatch();
  const organization = useSelector(selectOrganization);

  const { doAction: editOrganization, isProcessing } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (edits) => sdk.editCurrentOrganization(edits),
      failureMessage: "Failed to save workspace details",
      throwError: true,
    }),
    []
  );

  const { doAction: provisionDefaultLedgers } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => () => sdk.provisionWorkspaceDefaultLedgers(),
      failureMessage: "Failed to create workspace default ledgers",
      throwError: true,
    }),
    []
  );

  const defaultValues = useMemo<WorkspaceBasicsFormValues>(
    () => ({
      brandName: organization?.data?.name || "",
      website: organization?.data?.website || "",
      primaryColor: organization?.data?.primaryColor || "#000000",
      country: organization?.data?.country || "",
      logo: organization?.data?.logo || "",
      timezone: organization?.data?.timezone || "",
      primaryCurrency: organization?.data?.primaryCurrency || "GBP",
    }),
    [organization]
  );

  const primaryCurrencyLocked = isOrganizationDefaultLedgersComplete(
    organization?.data?.defaultLedgerAccounts
  );

  const handleSubmit = useCallback(
    async (data: WorkspaceBasicsFormValues) => {
      const updated = await editOrganization({
        data: {
          name: data.brandName,
          website: data.website || undefined,
          primaryColor: data.primaryColor,
          country: data.country,
          timezone: data.timezone,
          logo: data.logo,
          primaryCurrency: data.primaryCurrency,
        },
      });
      const orgAfterSave = updated ?? organization;
      if (updated) {
        dispatch(setOrganization(updated));
      }

      if (
        !isOrganizationDefaultLedgersComplete(
          orgAfterSave?.data?.defaultLedgerAccounts
        )
      ) {
        const provisioned = await provisionDefaultLedgers();
        if (provisioned) {
          dispatch(setOrganization(provisioned));
        }
      }

      onSaved();
    },
    [dispatch, editOrganization, onSaved, organization, provisionDefaultLedgers]
  );

  return (
    <div className="space-y-4">
      <WorkspaceBasicsForm
        organizationId={organization?.id}
        defaultValues={defaultValues}
        primaryCurrencyLocked={primaryCurrencyLocked}
        mode="wizard"
        isSubmitting={isProcessing}
        onSubmit={handleSubmit}
      />
      <div className="flex justify-end border-t pt-4">
        <Button
          type="submit"
          form="workspace-basics-form"
          disabled={isProcessing}
        >
          {isProcessing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save and continue
        </Button>
      </div>
    </div>
  );
}

export function WorkspaceLedgersStep({
  onProvisioned,
}: {
  onProvisioned?: () => void;
}) {
  const dispatch = useDispatch();
  const organization = useSelector(selectOrganization);
  const primaryCurrency = organization?.data?.primaryCurrency?.trim();
  const isProvisioned = isOrganizationDefaultLedgersComplete(
    organization?.data?.defaultLedgerAccounts
  );

  const { doAction: provisionDefaultLedgers, isProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => () => sdk.provisionWorkspaceDefaultLedgers(),
        failureMessage: "Failed to create workspace default ledgers",
        throwError: true,
      }),
      []
    );

  const handleProvision = useCallback(async () => {
    const updated = await provisionDefaultLedgers();
    if (updated) {
      dispatch(setOrganization(updated));
    }
    onProvisioned?.();
  }, [dispatch, onProvisioned, provisionDefaultLedgers]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Accounting currency & ledgers</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          TA Desk creates standard workspace ledgers for trade expenses and cash
          accounts in your primary currency
          {primaryCurrency ? ` (${primaryCurrency})` : ""}.
        </p>
      </div>

      {isProvisioned ? (
        <div className="rounded-lg border bg-green-50/50 p-4">
          <p className="text-sm font-medium text-green-900">
            Default ledgers are ready
          </p>
          <ul className="mt-2 space-y-1 text-sm text-green-900/80">
            {WORKSPACE_DEFAULT_LEDGER_DEFINITIONS.map((definition) => (
              <li key={definition.key}>• {definition.label}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4 rounded-lg border border-dashed p-4">
          <p className="text-sm text-muted-foreground">
            {primaryCurrency
              ? `Create ${WORKSPACE_DEFAULT_LEDGER_DEFINITIONS.length} default ledgers in ${primaryCurrency}.`
              : "Save a primary currency on the previous step first."}
          </p>
          <Button
            type="button"
            onClick={() => void handleProvision()}
            disabled={isProcessing || !primaryCurrency}
          >
            {isProcessing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Create workspace default ledgers
          </Button>
        </div>
      )}
    </div>
  );
}

export function WorkspaceCheckpointStep({
  checklistSummary,
}: {
  checklistSummary?: { completedCount: number; totalCount: number };
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/30 p-4">
        <h3 className="text-base font-semibold">Core workspace setup saved</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Next up: brand identity, quote pages, email templates, team invites,
          and payment integrations. You can continue setup anytime from the
          dashboard banner or Manage Workspace.
        </p>
        {checklistSummary ? (
          <p className="mt-3 text-sm">
            Workspace checklist:{" "}
            <span className="font-medium">
              {checklistSummary.completedCount} of {checklistSummary.totalCount}
            </span>{" "}
            items complete.
          </p>
        ) : null}
      </div>
    </div>
  );
}
